import React, { useEffect, useState } from 'react';
import { 
  Modal, 
  Box, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  CircularProgress,
  IconButton,
  Chip,
  Button,
  Snackbar,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { getEventRegisteredStudents, updateEventStudentAttendance } from '../../API/events';

interface EventStudentsModalProps {
  eventId: string;
  eventTitle: string;
  open: boolean;
  onClose: () => void;
  eventEndDate?: string; // Optional prop for event end date
  isCompleted?: boolean; // New prop to determine if event is completed
}

const modalStyle = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  maxWidth: 800,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  maxHeight: '80vh',
  overflow: 'auto'
};

interface StudentAttendance {
  [studentId: string]: boolean;
}

// Function to get event attendance from localStorage
const getStoredAttendance = (eventId: string): StudentAttendance => {
  try {
    const storedData = localStorage.getItem(`event_attendance_${eventId}`);
    return storedData ? JSON.parse(storedData) : {};
  } catch (e) {
    console.error('Error reading attendance from localStorage:', e);
    return {};
  }
};

// Function to store event attendance in localStorage
const storeAttendance = (eventId: string, attendance: StudentAttendance): void => {
  try {
    localStorage.setItem(`event_attendance_${eventId}`, JSON.stringify(attendance));
  } catch (e) {
    console.error('Error storing attendance to localStorage:', e);
  }
};

const EventStudentsModal = ({
  eventId, 
  eventTitle, 
  open, 
  onClose, 
  eventEndDate,
  isCompleted = false 
}) => {
  const [students, setStudents] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [attendance, setAttendance] = useState<StudentAttendance>({});
  const [changedAttendance, setChangedAttendance] = useState<string[]>([]);
  const [snackbar, setSnackbar] = useState<{open: boolean, message: string, severity: 'success' | 'error' | 'info'}>({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    if (open && eventId) {
      fetchStudents();
    }
  }, [open, eventId]);

  // Refresh when isCompleted changes
  useEffect(() => {
    if (open && eventId && isCompleted) {
      
      fetchStudents();
    }
  }, [isCompleted]);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const data = await getEventRegisteredStudents(eventId);
      
      setStudents(data || []);
      
      // Get stored attendance from localStorage
      const storedAttendance = getStoredAttendance(eventId);
      
      
      // Initialize attendance state for each student
      const initialAttendance: StudentAttendance = {};
      data.forEach((student: any) => {
        // Priority: 1. API attendance data 2. localStorage data 3. Default false
        const apiAttendance = student.attended === true;
        const storedStudentAttendance = storedAttendance[student._id] === true;
        
        initialAttendance[student._id] = apiAttendance || storedStudentAttendance || false;
      });
      
      setAttendance(initialAttendance);
      setChangedAttendance([]);
    } catch (error) {
      console.error('Error fetching registered students:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleAttendance = (studentId: string) => {
    if (!isCompleted) return; // Don't toggle if event is not completed
    
    setAttendance((prevAttendance) => {
      const newAttendance = {
        ...prevAttendance,
        [studentId]: !prevAttendance[studentId],
      };
      
      // Store in localStorage immediately to prevent data loss
      storeAttendance(eventId, newAttendance);
      
      return newAttendance;
    });
    
    // Track changed students
    setChangedAttendance(prev => {
      if (prev.includes(studentId)) {
        return prev;
      } else {
        return [...prev, studentId];
      }
    });
  };

  const saveAttendanceChanges = async () => {
    if (changedAttendance.length === 0) {
      setSnackbar({
        open: true,
        message: 'No attendance changes to save',
        severity: 'info'
      });
      return;
    }

    setSaving(true);
    try {
      // Process each changed student's attendance
      for (const studentId of changedAttendance) {
        await updateEventStudentAttendance(
          eventId,
          studentId,
          attendance[studentId]
        );
      }
      
      // Store the updated attendance in localStorage
      storeAttendance(eventId, attendance);
      
      setSnackbar({
        open: true,
        message: 'Student attendance saved successfully',
        severity: 'success'
      });
      
      // Clear the changed attendance list
      setChangedAttendance([]);
      
      // Refresh the student data to ensure we have the latest attendance info
      
      await fetchStudents();
    } catch (error) {
      console.error('Error saving attendance:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save attendance changes',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const getAttendanceStatus = (studentId: string) => {
    if (!isCompleted) {
      return (
        <Chip 
          label="Pending" 
          color="warning" 
          size="small"
          sx={{ minWidth: 85 }}
        />
      );
    }
    
    return attendance[studentId] ? (
      <Chip 
        label="Attended" 
        color="success" 
        size="small"
        sx={{ minWidth: 85 }}
        onClick={() => toggleAttendance(studentId)}
      />
    ) : (
      <Chip 
        label="Absent" 
        color="error" 
        size="small"
        sx={{ minWidth: 85 }}
        onClick={() => toggleAttendance(studentId)}
      />
    );
  };

  const handleCloseSnackbar = () => {
    setSnackbar({...snackbar, open: false});
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="event-students-modal-title"
    >
      <Box sx={modalStyle}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography id="event-students-modal-title" variant="h6" component="h2">
            Students Registered for: {eventTitle}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : students.length > 0 ? (
          <>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Roll No</TableCell>
                    <TableCell>Gender</TableCell>
                    <TableCell>School</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Parent's Name</TableCell>
                    <TableCell align="center">Attendance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {students.map((student) => (
                    <TableRow key={student._id}>
                      <TableCell>{student.name}</TableCell>
                      <TableCell>{student.roll_no}</TableCell>
                      <TableCell>{student.gender}</TableCell>
                      <TableCell>{student.school}</TableCell>
                      <TableCell>{student.contact}</TableCell>
                      <TableCell>{student.parents_name}</TableCell>
                      <TableCell align="center">
                        {getAttendanceStatus(student._id)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            {isCompleted && (
              <Box display="flex" justifyContent="flex-end" mt={2}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  startIcon={<SaveIcon />}
                  onClick={saveAttendanceChanges}
                  disabled={saving || changedAttendance.length === 0}
                >
                  {saving ? 'Saving...' : 'Save Attendance'}
                </Button>
              </Box>
            )}
          </>
        ) : (
          <Typography variant="body1" align="center" my={4}>
            No students registered for this event yet.
          </Typography>
        )}

        <Snackbar 
          open={snackbar.open} 
          autoHideDuration={6000} 
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Modal>
  );
};

export default EventStudentsModal; 